import React, {useCallback, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Box, IconButton, InputAdornment, TextField} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PageTitle from '../shared/PageTitle';
import {Profile} from '../../interfaces';
import {getAdminProfiles} from '../../api/adminAPI';
import ProfilesTable from './ProfilesTable';

const AdminProfileSearch: React.FC = () => {
  const {t} = useTranslation();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pending, setPending] = useState(true);

  const fetchData = useCallback(async (searchText: string) => {
    setPending(true);
    try {
      const data = await getAdminProfiles(
        searchText.trim() ? searchText.trim() : undefined,
      );
      setProfiles(data ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData(searchTerm);
    }, 1000);
    return () => clearTimeout(handle);
  }, [fetchData, searchTerm]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {
            pageName: t('pages.profiles'),
          })}
        </title>
      </Helmet>
      <Box>
        <PageTitle>{t('pages.profiles')}</PageTitle>
        <Box sx={{display: 'flex', gap: 2, mb: 2, alignItems: 'center'}}>
          <TextField
            size="small"
            placeholder={t('admin.searchProfiles') as string}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{minWidth: 300}}
            InputProps={{
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    aria-label={t('general.clear') as string}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
        <ProfilesTable profiles={profiles} loading={pending} />
      </Box>
    </>
  );
};

export default React.memo(AdminProfileSearch);
